import { useEffect, useState } from 'react'
import { useMidi } from '../hooks/useMidi'
import { useTuningStore } from '../store/tuningStore'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { AlertCircle, RefreshCw } from 'lucide-react'

export const MidiSelector = () => {
  const { getDevices, connect, disconnect, checkSupport } = useMidi()
  const { availableDevices, connectedDevice, midiSupported, setAvailableDevices } = useTuningStore()
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('')
  const [isConnecting, setIsConnecting] = useState(false)

  useEffect(() => {
    if (checkSupport()) {
      refreshDevices()
    }
  }, [checkSupport])

  const refreshDevices = async () => {
    const devices = await getDevices()
    setAvailableDevices(devices)
    if (devices.length > 0 && !selectedDeviceId) {
      setSelectedDeviceId(devices[0].id)
    }
  }

  const handleConnect = async () => {
    if (!selectedDeviceId) {
      console.warn('⚠️ No device selected')
      return
    }

    setIsConnecting(true)
    try {
      console.log(`🔌 Attempting to connect to MIDI device: ${selectedDeviceId}`)
      await connect(selectedDeviceId)
      console.log(`✅ Successfully connected to MIDI device`)
    } catch (error) {
      console.error('❌ Error connecting to MIDI device:', error)
      alert('Erreur de connexion au périphérique MIDI: ' + (error instanceof Error ? error.message : String(error)))
    } finally {
      setIsConnecting(false)
    }
  }

  const handleDisconnect = () => {
    disconnect()
    setSelectedDeviceId('')
  }

  if (!midiSupported) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Web MIDI API non supporté</AlertTitle>
        <AlertDescription>
          Cette fonctionnalité nécessite Chrome, Edge ou Opera. Firefox et Safari ne supportent pas Web MIDI API.
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <div className="space-y-3">
      <Select
        value={selectedDeviceId}
        onValueChange={setSelectedDeviceId}
        disabled={!!connectedDevice}
      >
        <SelectTrigger>
          <SelectValue placeholder="Sélectionner un périphérique" />
        </SelectTrigger>
        <SelectContent>
          {availableDevices.map((device) => (
            <SelectItem key={device.id} value={device.id}>
              {device.name} {device.manufacturer ? `(${device.manufacturer})` : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="flex  flex-col w-full gap-2">
        {connectedDevice ? (
          <>
            <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 rounded-md border border-emerald-200 flex-1">
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-emerald-800">{connectedDevice.name}</span>
            </div>
            <Button
              onClick={handleDisconnect}
              variant="destructive"
              size="sm"
            >
              Déconnecter
            </Button>
          </>
        ) : (
          <>
            <div className="flexitems-center gap-2 px-3 py-2 bg-muted rounded-md flex-1">
              <div className="w-2 h-2 bg-muted-foreground rounded-full"></div>
              <span className="text-sm text-muted-foreground">Non connecté</span>
            </div>
            <Button
              onClick={handleConnect}
              disabled={!selectedDeviceId || isConnecting}
              size="sm"
            >
              {isConnecting ? 'Connexion...' : 'Connecter'}
            </Button>
          </>
        )}
        <Button
          onClick={refreshDevices}
          variant="outline"
          size="icon"
          title="Actualiser la liste"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
